import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    cancelConsumption,
    deleteConsumption,
    getConsumptionList,
} from '../../services/ConsumptionNoteServices';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';

function ConsumptionNoteTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({
                value: p.id,
                label: p.plant_alias,
            })));
            setIsInitialized(true);
        });
    }, [cookies]);

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, []),
        [salesSettings, plants],
    );

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;
        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }
        setIsLoading(true);
        return getConsumptionList(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        })
            .then((response) => {
                setData({
                    records: (response.consumption_list || []).map((row) => ({
                        ...row,
                        consumption_no_display: row.consumption_no,
                        status_label: row.status === true || row.status === 1 || row.status === 'Active' ? 'Active' : 'Cancelled',
                        hide_row_edit: row.status === true || row.status === 1 || row.status === 'Active' ? 'No' : 'Yes',
                        hide_row_cancel: row.status === true || row.status === 1 || row.status === 'Active' ? 'No' : 'Yes',
                        hide_row_delete: row.status === true || row.status === 1 || row.status === 'Active' ? 'Yes' : 'No',
                    })),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load consumption notes.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings]);

    useEffect(() => {
        if (isInitialized) {
            fetchRecords();
        }
    }, [fetchRecords, isInitialized]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'consumption_no_display', headerName: 'Consumption No' },
        { fieldName: 'consumption_date', headerName: 'Date' },
        { fieldName: 'consumption_mode', headerName: 'Basis' },
        { fieldName: 'production_mode', headerName: 'Production' },
        { fieldName: 'total_concrete_qty', headerName: 'Concrete (cum)' },
        { fieldName: 'material_summary', headerName: 'Materials' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const handleOpen = (id) => {
        navigate(`/ConsumptionNoteEdit/${id}`);
    };

    const handleCancel = (id) => {
        Swal.fire({
            title: 'Cancel consumption note?',
            icon: 'warning',
            showCancelButton: true,
        }).then((result) => {
            if (!result.isConfirmed) return;
            cancelConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Cancelled', 'Consumption note cancelled.', 'success');
                    fetchRecords();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'));
        });
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete consumption note?',
            icon: 'warning',
            showCancelButton: true,
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Consumption note deleted.', 'success');
                    fetchRecords();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'));
        });
    };

    return (
        <>
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="STORE"
                    title="Consumption"
                    subtitle="Browse and manage material consumption vouchers for the selected plant and date range."
                    formLink="/ConsumptionNote"
                    addNewLabel="Main Form"
                    recordCount={data.records.length}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    modeLabel="VIEW MODE"
                >
                    <ReactTableWrapper
                        data={data.records}
                        columns={columns}
                        isLoading={isLoading}
                        onRowEdit={handleOpen}
                        onRowCancel={handleCancel}
                        onRowDelete={handleDelete}
                        compactRows
                        autoPinActionColumn={false}
                    />
                </ListPageLayout>
            </div>
            <SalesListSettingsModal
                show={showSettings}
                onClose={() => setShowSettings(false)}
                plants={plants}
                settings={salesSettings}
                onSave={(next) => {
                    const saved = saveSalesListSettings(cookies.myUserId, next);
                    setSalesSettings(saved);
                    setShowSettings(false);
                }}
            />
        </>
    );
}

export default ConsumptionNoteTable;
